import React, { Suspense } from 'react';
import { styled, useTheme } from '@mui/material/styles';
import { Typography, CircularProgress } from '@mui/material';
import { NODE_UI, stopPropagation } from './nodeUiStyles';

const ReactJson = React.lazy(() => import('react-json-view'));

/**
 * NodeJsonViewer — ReactJson collapsible tree for displaying JSON data.
 * Used by: API Req/Res (response), Webhook (sample), Trigger from Zapier (sample).
 */

const Label = styled(Typography)(({ theme }) => ({
  fontSize: NODE_UI.fontSize.label,
  fontWeight: NODE_UI.fontWeight.label,
  color: theme.palette.text.secondary,
  lineHeight: 1.4,
  marginBottom: '4px',
  userSelect: 'none',
}));

const ViewerContainer = styled('div')(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: NODE_UI.borderRadiusSm,
  padding: '8px 10px',
  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : '#fafafa',
  maxHeight: 300,
  overflowY: 'auto',
  fontSize: 12,
}));

const LoadingFallback = styled('div')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 16,
  gap: 8,
  fontSize: 12,
});

const NodeJsonViewer = React.memo(function NodeJsonViewer({
  label,
  data,
  collapsed = 1,
  displayDataTypes = false,
  enableClipboard = true,
  sx,
}) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const jsonData = typeof data === 'string' ? (() => {
    try { return JSON.parse(data); } catch { return { raw: data }; }
  })() : (data || {});

  return (
    <div onMouseDown={stopPropagation} onClick={stopPropagation} onPointerDown={stopPropagation}>
      {label && <Label>{label}</Label>}
      <ViewerContainer sx={sx}>
        <Suspense fallback={<LoadingFallback><CircularProgress size={14} /> Loading...</LoadingFallback>}>
          <ReactJson
            src={jsonData}
            collapsed={collapsed}
            displayDataTypes={displayDataTypes}
            enableClipboard={enableClipboard}
            theme={isDark ? 'monokai' : 'rjv-default'}
            style={{ backgroundColor: 'transparent', fontSize: 12 }}
            name={false}
          />
        </Suspense>
      </ViewerContainer>
    </div>
  );
});

export default NodeJsonViewer;
